import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNoteRequestPreview, useApplyNoteRequest, noteRequestKeys } from '../hooks/useNoteRequests';
import { AfterText, ListingStateChip, secRow, REVIEW_REASON_LABELS } from './NoteRequestBits';
import { AttachmentStrip } from './NoteAttachments';

// Staff "Apply" for one note request: shows the server's before → after
// preview per listing (composed from the inventory cache — 0 Skybox calls),
// lets the pricer untick listings, then runs the guarded writer. Results
// render in place and the modal stays open until closed so a failed write
// can't be missed (same shape as AiBulkReviewModal). Rows are snapshotted
// from the first preview load so a refetch after Apply can't drop them.
//
// Props: request (RequestView), enabled (note sync master switch; false =
//        preview only), onClose(), onMessage(msg, isError)
const APPLIABLE = new Set(['pending', 'failed']);

function NoteRequestApplyModal({ request, enabled, onClose, onMessage }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useNoteRequestPreview(request?.id);
  const apply = useApplyNoteRequest();

  const [rows, setRows] = useState(null);
  const [phrases, setPhrases] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [results, setResults] = useState({}); // inventoryId -> { state, before, after, error }
  const [globalError, setGlobalError] = useState('');

  useEffect(() => {
    if (data && !rows) {
      const list = data.listings || [];
      const hasTags = (data.tags || []).length > 0; // a tag call goes out even when the notes are unchanged
      setRows(list);
      setPhrases(data.phrases || []);
      setChecked(new Set(list.filter(l => APPLIABLE.has(l.state) && (l.changed || l.internalChanged || hasTags)).map(l => l.inventoryId)));
    }
  }, [data, rows]);

  const close = () => {
    queryClient.invalidateQueries({ queryKey: noteRequestKeys.all });
    onClose?.();
  };

  const tags = data?.tags || [];
  // Unchanged notes and no tags: Apply just marks the row verified
  const justVerify = (l) => !l.changed && !l.internalChanged && !tags.length;
  const canApplyRow = (l) => APPLIABLE.has(l.state) && !results[l.inventoryId];
  const selectable = (rows || []).filter(canApplyRow);
  const selected = selectable.filter(l => checked.has(l.inventoryId));
  const allChecked = selectable.length > 0 && selectable.every(l => checked.has(l.inventoryId));

  const toggle = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (allChecked) setChecked(new Set());
    else setChecked(new Set(selectable.map(l => l.inventoryId)));
  };

  const run = async () => {
    if (!selected.length) return;
    setGlobalError('');
    try {
      const res = await apply.mutateAsync({ id: request.id, inventoryIds: selected.map(l => l.inventoryId) });
      const next = { ...results };
      for (const r of res.data.results || []) next[r.inventoryId] = r;
      setResults(next);
      const applied = (res.data.results || []).filter(r => r.state === 'applied').length;
      const verified = (res.data.results || []).filter(r => r.state === 'verified').length;
      const failed = (res.data.results || []).filter(r => r.state === 'failed').length;
      onMessage?.(
        `Request #${request.id}: ${applied} applied · ${verified} already had it · ${failed} failed`,
        failed > 0 && applied === 0 && verified === 0
      );
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = code === 'DISABLED' ? 'Note sync is off — an admin must turn it on before notes can be written'
        : code === 'IN_FLIGHT' ? 'This request is already being applied — wait a moment and reload'
        : code === 'NO_LISTINGS' ? 'Nothing left to apply on this request'
        : err.response?.data?.error || err.message || 'Apply failed';
      setGlobalError(msg);
    }
  };

  const resultCell = (l) => {
    const r = results[l.inventoryId];
    if (!r) return null;
    if (r.state === 'applied' || r.state === 'verified') {
      return (
        <span className="nr-result-ok">
          ✓ {r.state === 'verified' ? 'already present' : 'written'}
          <div className="nr-result-diff">
            <span className="nr-diff-before">{r.before || <em className="nr-muted">(empty)</em>}</span>
            <span className="nr-diff-arrow"> → </span>
            <AfterText before={r.before} after={r.after} />
          </div>
        </span>
      );
    }
    if (r.state === 'failed') return <span className="nr-result-bad">✗ {r.error || 'write failed'}</span>;
    return <span className="nr-result-muted">{r.state}{r.error ? ` — ${r.error}` : ''}</span>;
  };

  const freeText = request?.status === 'needs_review' && request?.reviewReason === 'free_text';
  const reasonNote = request?.reviewReason && REVIEW_REASON_LABELS[request.reviewReason];

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal modal-content" style={{ maxWidth: 980, width: '95%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>{request.tags?.length ? 'Apply note/tags' : 'Apply note'} — request #{request.id} · {request.orderNumber}</h2>
          <button className="modal-close" onClick={close}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <div className="ai-admin-note" style={{ marginBottom: 8 }}>
            {rows && !phrases.length && tags.length ? null : <>
              Adds {phrases.length ? phrases.map((p, i) => <code key={i} className="nr-phrase">{p}</code>) : '…'} to each listing's public notes
              (skipped when already present).
            </>}
            {(data?.internalPhrases || []).length ? <> Also adds {(data.internalPhrases || []).map((p, i) => <code key={i} className="nr-phrase">{p}</code>)} to the listing's <b>internal</b> notes (staff only).</> : null}
            {tags.length ? <> Will add tags {tags.map(t => <code key={t} className="nr-phrase">{t}</code>)} in Skybox (staff only).</> : null}
            {' '}{request.submittedByName ? `Requested by ${request.submittedByName}.` : ''}
            {data?.cacheSyncedAgoSeconds != null ? ` Preview from the inventory cache (synced ${data.cacheSyncedAgoSeconds}s ago); the writer re-reads each listing before writing.` : ''}
          </div>
          {reasonNote && (
            <div className={`ai-stale-banner ${freeText ? 'nr-freetext-warn' : ''}`} style={{ marginBottom: 10 }}>
              In review because: <b>{reasonNote}</b>.
              {freeText
                ? ' This is buyer-typed text that will be shown to ticket buyers on marketplaces — read the wording; pressing Apply is the confirmation.'
                : ' Check the listings below are really this order before applying.'}
            </div>
          )}
          {request.comment && (
            <div className="ai-admin-note" style={{ marginBottom: 8 }}>Buyer comment: “{request.comment}”</div>
          )}
          {/* View-only: `request` is the row snapshot from when the modal opened */}
          {(request.attachments || []).length > 0 && (
            <div className="ai-admin-note" style={{ marginBottom: 8 }}>
              Photos ({request.attachments.length}) — click to enlarge:
              <AttachmentStrip request={request} />
            </div>
          )}

          {isLoading || !rows ? (
            <div className="loading">Loading preview…</div>
          ) : error ? (
            <div className="message message-error">{error.response?.data?.error || 'Failed to load preview'}</div>
          ) : rows.length === 0 ? (
            <div className="ai-summary-empty" style={{ padding: 12 }}>No listings on this request yet — use Link listing first.</div>
          ) : (
            <table className="bulk-review-table nr-preview-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}><input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={!selectable.length || apply.isPending} /></th>
                  <th>Listing</th>
                  <th>State</th>
                  <th>Current public notes</th>
                  <th>After</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(l => {
                  const r = results[l.inventoryId];
                  const selectableRow = canApplyRow(l);
                  return (
                    <tr key={l.listingId ?? l.inventoryId} style={{ opacity: r ? 0.85 : 1 }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectableRow && checked.has(l.inventoryId)}
                          disabled={!selectableRow || apply.isPending}
                          onChange={() => toggle(l.inventoryId)}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {secRow(l)}{l.seats ? ` · ${l.seats}` : ''}
                        <div className="nr-muted" style={{ fontSize: 11 }}>#{l.inventoryId}</div>
                      </td>
                      <td><ListingStateChip state={r?.state || l.state} /></td>
                      <td className="nr-notes-cell">{l.before ? l.before : <em className="nr-muted">(empty)</em>}</td>
                      <td className="nr-notes-cell">
                        <AfterText before={l.before} after={l.after} />
                        {justVerify(l) && APPLIABLE.has(l.state) && (
                          <div className="nr-muted" style={{ fontSize: 11 }}>already contains phrase — will be marked verified</div>
                        )}
                        {l.internalChanged && (
                          <div className="nr-muted" style={{ fontSize: 11 }} title={`Internal notes: ${l.internalBefore || '(empty)'} → ${l.internalAfter}`}>
                            + internal notes: <AfterText before={l.internalBefore || ''} after={l.internalAfter || ''} />
                          </div>
                        )}
                        {l.tooLong && (
                          <div className="nr-result-bad" style={{ fontSize: 11 }}>would exceed the public-notes limit — shorten in Skybox first</div>
                        )}
                      </td>
                      <td>{resultCell(l)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {globalError && <div className="ai-inline-error" style={{ marginTop: 10 }}>{globalError}</div>}
        </div>
        <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: '1px solid var(--color-border)' }}>
          <span className="ai-admin-note">
            {enabled === false
              ? 'Note sync is off — an admin must turn it on (Admin → Note Sync) before notes can be written'
              : <>{selected.length} selected{selected.some(justVerify) ? ' · unchanged rows will just be verified' : ''}</>}
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={close} disabled={apply.isPending}>Close</button>
          <button className="btn-primary" onClick={run} disabled={apply.isPending || !selected.length || enabled === false} title={enabled === false ? 'Note sync is off' : ''}>
            {apply.isPending ? 'Applying… (about a second per listing)' : `Apply ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NoteRequestApplyModal;
